import PropTypes from 'prop-types';

const SkeletonLoader = ({
  variant = "rectangular",
  width = "100%",
  height = "20px",
  className = "",
  animate = true,
}) => {
  const baseClasses = `bg-gray-200 dark:bg-gray-700 ${
    animate ? "animate-pulse" : ""
  }`;

  const variantClasses = {
    rectangular: "rounded",
    circular: "rounded-full",
    text: "rounded h-4",
    card: "rounded-lg",
  };

  const style = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
      role="status"
      aria-label="Loading content"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

// Predefined skeleton components for common use cases
export const SkeletonText = ({ lines = 3, className = "" }) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, index) => (
      <SkeletonLoader
        key={index}
        variant="text"
        width={index === lines - 1 ? "75%" : "100%"}
      />
    ))}
  </div>
);

export const SkeletonCard = ({ className = "" }) => (
  <div className={`p-4 border border-gray-200 rounded-lg ${className}`}>
    <SkeletonLoader variant="rectangular" height="200px" className="mb-4" />
    <SkeletonLoader variant="text" height="24px" className="mb-2" />
    <SkeletonText lines={2} />
  </div>
);

export const SkeletonAvatar = ({ size = "md", className = "" }) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
    xl: "w-24 h-24",
  };

  return (
    <SkeletonLoader
      variant="circular"
      className={`${sizeClasses[size]} ${className}`}
    />
  );
};

SkeletonLoader.propTypes = {
  variant: PropTypes.oneOf(["rectangular", "circular", "text", "card"]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  animate: PropTypes.bool,
};

SkeletonText.propTypes = {
  lines: PropTypes.number,
  className: PropTypes.string,
};

SkeletonCard.propTypes = {
  className: PropTypes.string,
};

SkeletonAvatar.propTypes = {
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  className: PropTypes.string,
};

export default SkeletonLoader;